import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripeConfig, createPaymentIntent, createOrGetPaymentLinkPaymentIntent, markPaymentLinkPaid, getPaymentLinkData } from '../services/api';
import { theme } from '../config/theme';
import { CreditCardIcon, CheckIcon, XIcon, PackageIcon, FileTextIcon, DollarSignIcon } from 'lucide-react';

// Initialize Stripe
let stripePromise: Promise<any> | null = null;

const getStripe = async () => {
  if (!stripePromise) {
    const config = await getStripeConfig();
    if (config.enabled && config.publishable_key) {
      stripePromise = loadStripe(config.publishable_key);
    }
  }
  return stripePromise;
};

interface PaymentFormProps {
  amount: number;
  bookingId: string;
  description: string;
  clientSecret: string;
  paymentLinkId?: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ amount, bookingId, description, clientSecret, paymentLinkId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success?booking_id=${bookingId}`,
        },
        redirect: 'if_required',
      });

      if (submitError) {
        setError(submitError.message || 'Payment failed');
        setProcessing(false);
      } else {
        // Mark payment link as paid if this was from a payment link
        if (paymentLinkId && paymentIntent) {
          try {
            await markPaymentLinkPaid(paymentLinkId, paymentIntent.id);
          } catch (err) {
            console.error('Error marking payment link as paid:', err);
          }
        }
        
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setProcessing(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckIcon className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
        <p className="text-gray-600 mb-6">Your payment has been processed successfully.</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-3 text-white rounded-md font-medium"
          style={{ backgroundColor: theme.colors.primary[600] }}
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start">
          <XIcon className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full px-6 py-3 text-white rounded-md font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        style={{ backgroundColor: theme.colors.primary[600] }}
        onMouseEnter={(e) => !processing && (e.currentTarget.style.opacity = '0.9')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
      >
        {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
      </button>

      <p className="text-xs text-center text-gray-500">
        Your payment is secure and encrypted. We never store your card details.
      </p>
    </form>
  );
};

const Payment: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [clientSecret, setClientSecret] = useState<string>('');
  const [stripeReady, setStripeReady] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);
  const initializingRef = useRef(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate initialization using ref (survives React Strict Mode double-invoke)
    if (initializedRef.current || initializingRef.current) {
      return;
    }

    const initializePayment = async () => {
      if (!uuid) {
        setError('Invalid payment link');
        setLoading(false);
        return;
      }

      initializingRef.current = true;

      try {
        // Fetch payment link data by UUID
        const linkData = await getPaymentLinkData(uuid);
        setPaymentData(linkData);

        // Amount is stored in cents in the database
        const amountInCents = parseFloat(linkData.amount);
        const bookingId = linkData.booking_id;
        const customerEmail = linkData.customer_email || '';
        const customerName = linkData.customer_name || '';

        // Initialize Stripe
        const stripe = await getStripe();
        if (!stripe) {
          setError('Payment processing is not available');
          setLoading(false);
          return;
        }
        setStripeReady(true);

        // Create or retrieve payment intent for payment link (Stripe expects cents)
        const response = await createOrGetPaymentLinkPaymentIntent(uuid, {
          amount: amountInCents,
          currency: 'usd',
          customer_email: customerEmail,
          customer_name: customerName,
          booking_metadata: {
            booking_id: bookingId,
            type: 'remaining_balance'
          }
        });

        setClientSecret(response.client_secret);
        initializedRef.current = true;
      } catch (err: any) {
        console.error('Error initializing payment:', err);
        if (err.response?.status === 404) {
          setError('Payment link not found or has expired');
        } else if (err.response?.status === 410) {
          setError('This payment link has already been used');
        } else {
          setError(err.response?.data?.detail || 'Failed to initialize payment');
        }
      } finally {
        setLoading(false);
        initializingRef.current = false;
      }
    };

    initializePayment();
  }, [uuid]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.colors.background.secondary }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: theme.colors.primary[600] }}></div>
          <p className="text-gray-600">Loading payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.colors.background.secondary }}>
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XIcon className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/Home')}
              className="px-6 py-2 text-white rounded-md font-medium"
              style={{ backgroundColor: theme.colors.primary[600] }}
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!stripeReady || !clientSecret) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.colors.background.secondary }}>
        <div className="text-center">
          <p className="text-gray-600">Initializing payment...</p>
        </div>
      </div>
    );
  }

  // Payment link amounts are all stored in cents.
  const totalCents = parseFloat(paymentData?.total_price) || 0;
  const deliveryCents = parseFloat(paymentData?.delivery_fee) || 0;
  const deliveryMiles = parseFloat(paymentData?.delivery_distance_miles) || 0;
  const taxCents = parseFloat(paymentData?.tax_amount) || 0;
  // Bookings made before receipts broke tax out have no snapshotted tax, so
  // fall back to a rental line that carries it rather than inventing a figure
  // from today's rate. Either way the lines shown always sum to the total.
  const hasTaxBreakdown = taxCents > 0;
  const rentalCents = totalCents - deliveryCents - taxCents;

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: theme.colors.background.secondary }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex items-center justify-center mb-6">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.colors.primary[100] }}>
              <CreditCardIcon className="w-6 h-6" style={{ color: theme.colors.primary[600] }} />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">Complete Payment</h1>
          <p className="text-center text-gray-600 mb-8">
            Secure payment powered by Stripe
          </p>

          {paymentData && (
            <div className="mb-8 space-y-4">
              {/* Product Name */}
              <div className="flex items-start">
                <PackageIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Product</p>
                  <p className="text-base font-semibold text-gray-900">{paymentData.product_name}</p>
                </div>
              </div>

              {/* Payment Note */}
              {paymentData.description && (
                <div className="flex items-start">
                  <FileTextIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Description</p>
                    <p className="text-base text-gray-900">{paymentData.description}</p>
                  </div>
                </div>
              )}

              {/* Payment Breakdown */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center">
                  <DollarSignIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <p className="text-sm font-medium text-gray-500">Payment Summary</p>
                </div>
                <div className="ml-8 space-y-1">
                  {paymentData.discount_amount && parseFloat(paymentData.discount_amount) > 0 ? (
                    <>
                      {/* total_price already has the discount deducted, so the
                          pre-discount price is total + discount. */}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Price Before Discount:</span>
                        <span className="font-medium text-gray-900">${((parseFloat(paymentData.total_price) + parseFloat(paymentData.discount_amount)) / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600">Discount:</span>
                        <span className="font-medium text-green-600">-${(parseFloat(paymentData.discount_amount) / 100).toFixed(2)}</span>
                      </div>
                    </>
                  ) : null}
                  {/* Break the total into its true parts. Skipped entirely when
                      there is neither a delivery fee nor a stored tax figure,
                      since a lone rental line would just repeat the total. */}
                  {deliveryCents > 0 || hasTaxBreakdown ? (
                    <>
                      <div className="flex justify-between text-sm">
                        {/* Without a stored tax figure the rental line still
                            carries the tax, so label it honestly. */}
                        <span className="text-gray-600">{hasTaxBreakdown ? 'Rental:' : 'Rental + Tax:'}</span>
                        <span className="font-medium text-gray-900">${(rentalCents / 100).toFixed(2)}</span>
                      </div>
                      {deliveryCents > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Delivery{deliveryMiles > 0 ? ` (${Math.round(deliveryMiles)} mi)` : ''}:
                          </span>
                          <span className="font-medium text-gray-900">${(deliveryCents / 100).toFixed(2)}</span>
                        </div>
                      )}
                      {hasTaxBreakdown && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Tax:</span>
                          <span className="font-medium text-gray-900">${(taxCents / 100).toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  ) : null}
                  <div className={`flex justify-between text-sm${deliveryCents > 0 || hasTaxBreakdown ? ' border-t border-gray-300 pt-2 mt-2' : ''}`}>
                    <span className="text-gray-600">Total Price:</span>
                    <span className="font-medium text-gray-900">${(totalCents / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Previous Payments:</span>
                    <span className="font-medium text-gray-900">-${(parseFloat(paymentData.deposit_paid) / 100).toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between">
                    <span className="text-base font-semibold text-gray-900">Amount Due:</span>
                    <span className="text-xl font-bold" style={{ color: theme.colors.primary[700] }}>
                      ${(parseFloat(paymentData.amount) / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: theme.colors.primary[600],
                },
              },
            }}
          >
            <PaymentForm
              amount={paymentData ? parseFloat(paymentData.amount) / 100 : 0}
              bookingId={paymentData?.booking_id || ''}
              description={paymentData?.description || 'Payment'}
              clientSecret={clientSecret}
              paymentLinkId={uuid || ''}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default Payment;
